/*
  component: collapsible-section
  title: Collapsible Section
  props:
    - summary: header label (required)
    - hint: optional form-text shown at top of body
    - open: expand on load when present
  events:
    - collapsible-toggle: detail { open: boolean }
  deps: none
*/
class CollapsibleSection extends HTMLElement {
  constructor() {
    super();
    this._built = false;
    this._onToggle = this._onToggle.bind(this);
    this._onArrowAnimationEnd = this._onArrowAnimationEnd.bind(this);
  }

  static get observedAttributes() {
    return ['summary', 'hint', 'open'];
  }

  connectedCallback() {
    if (!this._built) {
      this._build();
    } else {
      this._syncFromAttributes();
    }
  }

  disconnectedCallback() {
    this._details?.removeEventListener('toggle', this._onToggle);
    this._arrow?.removeEventListener('animationend', this._onArrowAnimationEnd);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this._built) {
      return;
    }
    this._syncFromAttributes();
  }

  _build() {
    const childNodes = Array.from(this.childNodes);

    this._details = document.createElement('details');
    this._details.className = 'collapsible-section-panel';

    const summaryEl = document.createElement('summary');
    summaryEl.className = 'collapsible-section-summary';

    this._arrow = document.createElement('span');
    this._arrow.className = 'collapsible-section-arrow';
    this._arrow.setAttribute('aria-hidden', 'true');
    this._arrow.textContent = '▸';
    this._arrow.addEventListener('animationend', this._onArrowAnimationEnd);

    this._label = document.createElement('span');
    this._label.className = 'collapsible-section-label';

    summaryEl.appendChild(this._arrow);
    summaryEl.appendChild(this._label);

    this._body = document.createElement('div');
    this._body.className = 'collapsible-section-body';

    this._bodyInner = document.createElement('div');
    this._bodyInner.className = 'collapsible-section-body-inner';

    childNodes.forEach((node) => {
      this._bodyInner.appendChild(node);
    });

    this._body.appendChild(this._bodyInner);
    this._details.appendChild(summaryEl);
    this._details.appendChild(this._body);

    this.replaceChildren(this._details);
    this._details.addEventListener('toggle', this._onToggle);

    this._built = true;
    this._syncFromAttributes();
  }

  _syncFromAttributes() {
    if (!this._built) {
      return;
    }

    const summaryText = this.getAttribute('summary') || 'Section';
    if (this._label) {
      this._label.textContent = summaryText;
    }

    this._syncHint();

    const shouldOpen = this.hasAttribute('open');
    if (this._details.open !== shouldOpen) {
      this._details.open = shouldOpen;
    }

    this._syncOpenState();
  }

  _syncHint() {
    const hint = this.getAttribute('hint') || '';

    if (!hint) {
      if (this._hintEl) {
        this._hintEl.remove();
        this._hintEl = null;
      }
      return;
    }

    if (!this._hintEl) {
      this._hintEl = document.createElement('p');
      this._hintEl.className = 'form-text collapsible-section-hint';
      this._bodyInner.prepend(this._hintEl);
    }

    this._hintEl.textContent = hint;
  }

  _onToggle() {
    const open = this._details.open;

    if (open) {
      this.setAttribute('open', '');
      this._arrow?.classList.add('collapsible-section-arrow-wiggle');
    } else {
      this.removeAttribute('open');
    }

    this._syncOpenState();

    this.dispatchEvent(new CustomEvent('collapsible-toggle', {
      bubbles: true,
      detail: { open }
    }));
  }

  _onArrowAnimationEnd(event) {
    if (event.animationName === 'collapsible-arrow-wiggle') {
      this._arrow?.classList.remove('collapsible-section-arrow-wiggle');
    }
  }

  _syncOpenState() {
    if (!this._details) {
      return;
    }

    const open = this._details.open;
    this.dataset.open = open ? 'true' : 'false';
    this._details.dataset.open = open ? 'true' : 'false';
  }
}

customElements.define('collapsible-section', CollapsibleSection);
